/*
 * Copyright (c) 2019 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import moment from 'moment';
import { Injectable, Injector } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { ChangedEvent, hasValue } from '@typescript-kit/core';
import { PersistenceService } from '@angular-kit/core';
import { ViewService } from '@angular-kit/view';
import {
  ActionModel,
  Alignments,
  ComponentModel,
  ContainerModel,
  DropdownModel,
  KeyCodes,
  SelectInputModel,
  TableCellModel,
  TableColumnModel,
  TableModel,
  TableRowModel,
  TextInputModel,
  TextModel,
  ValueModel,
  ViewComponentKey,
} from '@typescript-kit/view';
import {
  Project,
  TimeRecordFieldKey,
  TimeRecordModel,
  TimeRecordTypes,
  TargetHoursModel,
  TargetHoursTypes,
  PublicHoliday,
  TimeRecordingValidationKey,
  getProjectTree,
  ProjectTree,
  ProjectModel
} from '@teamworks/global';
import { TimeRecordingService } from '../service/time-recording.service';
import { TimeRecordingViewFieldKey, TimeRecordingViewModel } from '../view-model/time-recording.view-model';
import { TimeRecordingPersistenceKey } from '../key/time-recording-persistence.key';
import { TimeRecordingComponentKey, TimeRecordingComponentKey as ComponentKey } from '../key/time-recording-component.key';
import { AutoCompleteInputModel } from '../../shared/component-model/auto-complete-input.model';
import { Subscription } from 'rxjs';
import { EntityService } from '../../shared/service/entity.service';
import { EmployeeTimeRecordingSharedViewService } from './employee-time-recording-shared.view-service';
import { TimeRecordingViewKey } from '../key/time-recording-view.key';
import { ProjectAutoCompleteInputModel } from '../component-model/project-auto-complete-input.component-model';

// if column index is modified:
// then update order of column model array
// then update order of row cell model array
class ColumnIndex {
  static readonly INSERT_ROW = 0;
  static readonly REMOVE_ROW = 1;
  static readonly DAY = 2;
  static readonly FROM = 3;
  static readonly TO = 4;
  static readonly HOURS = 5;
  static readonly TYPE = 6;
  static readonly DAY_ACTUAL = 7;
  static readonly DAY_EXPECTED = 8;
  static readonly BALANCE = 9;
  static readonly WEEK_HOURS = 10;
  static readonly PROJECT = 11;
  static readonly COMMENT = 12;
  static readonly OVERTIME = 13;
  static readonly OVERTIME_50 = 14;
  static readonly OVERTIME_100 = 15;
}

class ColumnKey {
  static readonly [ColumnIndex.DAY] = `${ComponentKey.TIME_RECORDING_TABLE}/column/day`;
  static readonly [ColumnIndex.FROM] = `${ComponentKey.TIME_RECORDING_TABLE}/column/from`;
  static readonly [ColumnIndex.TO] = `${ComponentKey.TIME_RECORDING_TABLE}/column/to`;
  static readonly [ColumnIndex.HOURS] = `${ComponentKey.TIME_RECORDING_TABLE}/column/hours`;
  static readonly [ColumnIndex.TYPE] = `${ComponentKey.TIME_RECORDING_TABLE}/column/type`;
  static readonly [ColumnIndex.DAY_ACTUAL] = `${ComponentKey.TIME_RECORDING_TABLE}/column/actual`;
  static readonly [ColumnIndex.DAY_EXPECTED] = `${ComponentKey.TIME_RECORDING_TABLE}/column/expected`;
  static readonly [ColumnIndex.BALANCE] = `${ComponentKey.TIME_RECORDING_TABLE}/column/balance`;
  static readonly [ColumnIndex.WEEK_HOURS] = `${ComponentKey.TIME_RECORDING_TABLE}/column/weekHours`;
  static readonly [ColumnIndex.PROJECT] = `${ComponentKey.TIME_RECORDING_TABLE}/column/project`;
  static readonly [ColumnIndex.COMMENT] = `${ComponentKey.TIME_RECORDING_TABLE}/column/comment`;
  static readonly [ColumnIndex.OVERTIME] = `${ComponentKey.TIME_RECORDING_TABLE}/column/overtime`;
  static readonly [ColumnIndex.OVERTIME_50] = `${ComponentKey.TIME_RECORDING_TABLE}/column/overtime50`;
  static readonly [ColumnIndex.OVERTIME_100] = `${ComponentKey.TIME_RECORDING_TABLE}/column/overtime100`;
}

@Injectable()
export class EmployeeTimeRecordingTableViewService extends ViewService<TimeRecordingViewModel, TableModel> {
  private readonly timeRecordTypeSelectOptions = {
    values: [
      null,
      TimeRecordTypes.WORK,
      TimeRecordTypes.PUBLIC_HOLIDAY,
      TimeRecordTypes.SICK,
      TimeRecordTypes.SHORT_TIME_WORK,
      TimeRecordTypes.BREAK,
      TimeRecordTypes.HOLIDAY,
      TimeRecordTypes.COMP_TIME,
    ],
    formattedValues: [
      '-',
      this.textService.getText(`time-recording/model/time-record-type/value/work`),
      this.textService.getText(`time-recording/model/time-record-type/value/public-holiday`),
      this.textService.getText(`time-recording/model/time-record-type/value/sick`),
      this.textService.getText(`time-recording/model/time-record-type/value/short-time-work`),
      this.textService.getText(`time-recording/model/time-record-type/value/break`),
      this.textService.getText(`time-recording/model/time-record-type/value/holiday`),
      this.textService.getText(`time-recording/model/time-record-type/value/comp-time`),
    ]
  };

  private timeRecordProjectOptions: ProjectTree = {};

  private readonly projectNotVisibleText = this.textService.getText(`${TimeRecordingViewKey.TIME_RECORDING_TABLE}/project-not-visible`);

  private readonly activatedRoute = this.injector.get(ActivatedRoute);
  private readonly persistenceService = this.injector.get(PersistenceService);
  private readonly entityService = this.injector.get(EntityService);
  private readonly timeRecordingService = this.injector.get(TimeRecordingService);
  private readonly sharedViewService = this.injector.get(EmployeeTimeRecordingSharedViewService);

  private readonly hiddenColumns: Set<number>;
  private readonly refreshingRows: Set<number>;

  private entityEventSubscription: Subscription;

  constructor(injector: Injector) {
    super(injector);

    this.hiddenColumns = this.loadHiddenColumns();
    this.refreshingRows = new Set<number>();
  }

  initialize(viewModel?: TimeRecordingViewModel): void {
    super.initialize(viewModel);
    this.entityEventSubscription = this.entityService.entityEvent.subscribe((event) => {
      if (event.entity instanceof TimeRecordModel) {
        const timeRecordIndex = this.viewModel.timeRecordList.indexOf(event.entity);
        if (timeRecordIndex < 0) {
          return;
        }
        const rowModel = this.componentModel.getRow(timeRecordIndex + 1);
        this.entityService.setEntityEventTag(rowModel, event);
      } else if (event.entity instanceof TargetHoursModel) {
        const timeRecordIndex = this.viewModel.timeRecordList.firstIndex((item) => item.date === event.entity.date);
        if (timeRecordIndex < 0) {
          return;
        }
        const cellModel = this.componentModel.getCell(timeRecordIndex + 1, ColumnIndex.DAY_EXPECTED);
        this.entityService.setEntityEventTag(cellModel, event);
      }
    });
  }

  finalize(): void {
    this.entityEventSubscription.unsubscribe();
    super.finalize();
  }

  protected onViewModelPropertyChanged(event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      if (event.originalEvent.source === this.viewModel.timeRecordList) {
        this.onTimeRecordingListPropertyChanged(event.originalEvent);
      } else if (event.originalEvent.source === this.viewModel.targetHoursMap) {
        this.onTargetHoursListItemChanged(event.originalEvent);
      } else if (event.originalEvent.source === this.viewModel.projectList) {
        this.onTargetHoursListItemChanged(event.originalEvent);
      }
      return;
    }
    if (event.changes[TimeRecordingViewFieldKey.TIME_RECORD_LIST]) {
      this.refreshComponentModel(this.componentModel);
    }
    if (event.changes[TimeRecordingViewFieldKey.PROJECT_LIST]) {
      const projects = this.viewModel.projectList.filter(project => project.isActive);
      this.timeRecordProjectOptions = getProjectTree(projects, 'name');
      this.refreshComponentModel(this.componentModel);
    }
  }

  private onTimeRecordingListPropertyChanged(event: ChangedEvent) {
    if (event.originalEvent !== event) {
      if (event.originalEvent.source instanceof TimeRecordModel) {
        this.onTimeRecordPropertyChanged(event.originalEvent);
      }
      return;
    }
    this.refreshComponentModel(this.componentModel);
  }

  private onTargetHoursListItemChanged(event: ChangedEvent) {
    if (event.originalEvent !== event) {
      if (event.originalEvent.source instanceof TargetHoursModel) {
        this.onTargetHoursPropertyChanged(event.originalEvent);
      }
      return;
    }
    // tslint:disable-next-line:forin
    for (const key in event.changes) {
      this.onTargetHoursChanged(event.changes[key].currentValue, event.changes[key].originalValue);
    }
  }

  private onTargetHoursPropertyChanged(event: ChangedEvent) {
    this.onTargetHoursChanged(event.source);
  }

  private onTargetHoursChanged(targetHours: TargetHoursModel, originalTargetHours?: TargetHoursModel) {
    const date = targetHours ? targetHours.date : originalTargetHours.date;
    const timeRecordIndex = this.viewModel.timeRecordList.firstIndex(item => item.date === date);
    if (timeRecordIndex < 0) {
      return;
    }
    const timeRecord = this.viewModel.timeRecordList.get(timeRecordIndex);
    const firstRecordOfWeekIndex = this.getIndexOfFirstRecordOfWeek(timeRecordIndex);
    const isFirstRecordOfWeek = timeRecordIndex === firstRecordOfWeekIndex;
    const targetHoursCell = this.componentModel.getCell(timeRecordIndex + 1, ColumnIndex.DAY_EXPECTED);
    targetHoursCell.content.set('value', targetHours ? targetHours.value : null);
    this.refreshRow(timeRecord, timeRecordIndex, true, isFirstRecordOfWeek);
  }

  private onTimeRecordPropertyChanged(event: ChangedEvent) {
    const timeRecord = event.source as TimeRecordModel;
    const recordIndex = this.viewModel.timeRecordList.indexOf(timeRecord);
    if (recordIndex < 0) {
      return;
    }
    const firstRecordOfDayIndex = this.getIndexOfFirstRecordOfDay(recordIndex);
    const isFirstRecordOfDay = recordIndex === firstRecordOfDayIndex;
    const firstRecordOfWeekIndex = this.getIndexOfFirstRecordOfWeek(firstRecordOfDayIndex);
    const isFirstRecordOfWeek = recordIndex === firstRecordOfWeekIndex;
    this.refreshRow(timeRecord, recordIndex, isFirstRecordOfDay, isFirstRecordOfWeek);
    if (event.changes[TimeRecordFieldKey.HOURS]
      || event.changes[TimeRecordFieldKey.TYPE]
      || event.changes[TimeRecordFieldKey.OVERTIME]
      || event.changes[TimeRecordFieldKey.OVERTIME_50]
      || event.changes[TimeRecordFieldKey.OVERTIME_100]
    ) {
      if (event.changes[TimeRecordFieldKey.HOURS]) {
        if (typeof timeRecord.hours !== 'number') {
          if (hasValue(timeRecord.type)) {
            timeRecord.type = null;
          }
        } else {
          if (!hasValue(timeRecord.type)) {
            timeRecord.type = TimeRecordTypes.WORK;
          }
        }
      }
      if (!isFirstRecordOfDay) {
        const firstRecordOfDay = this.viewModel.timeRecordList.get(firstRecordOfDayIndex);
        this.refreshRow(firstRecordOfDay, firstRecordOfDayIndex, true, firstRecordOfDayIndex === firstRecordOfWeekIndex);
      }
      if (!isFirstRecordOfWeek && firstRecordOfDayIndex !== firstRecordOfWeekIndex) {
        const firstRecordOfWeek = this.viewModel.timeRecordList.get(firstRecordOfWeekIndex);
        this.refreshRow(firstRecordOfWeek, firstRecordOfWeekIndex, true, true);
      }
    }
  }

  protected createComponentModel(): TableModel {
    console.timeStamp('EmployeeTimeRecordingTableViewService.createComponentModel');
    return new TableModel({
      tags: ['app-time-record-table'],
      isHidden: !this.viewModel.timeRecordList,
      columns: this.createColumnModels(),
      rows: this.createRowModels(),
      cells: this.createCellModels(),
      columnHeaderCount: 1,
      rowHeaderCount: 1,
    });
  }

  protected refreshComponentModel(tableModel: TableModel) {
    console.timeStamp('EmployeeTimeRecordingTableViewService.refreshComponentModel');
    tableModel.setValues({
      isHidden: !this.viewModel.timeRecordList,
      columns: this.refreshColumnModels(tableModel.columnArray),
      rows: this.refreshRowModels(tableModel.rowArray),
      cells: this.createCellModels()
    });
  }

  private createColumnModels() {
    const columns = [
      new TableColumnModel({ index: ColumnIndex.INSERT_ROW, tags: ['app-action-column'] }),
      new TableColumnModel({ index: ColumnIndex.REMOVE_ROW, tags: ['app-action-column'] }),
      new TableColumnModel({ index: ColumnIndex.DAY, tags: ['app-day'] }),
      new TableColumnModel({ index: ColumnIndex.FROM, tags: ['app-from'] }),
      new TableColumnModel({ index: ColumnIndex.TO, tags: ['app-to'] }),
      new TableColumnModel({ index: ColumnIndex.HOURS, tags: ['app-hours'] }),
      new TableColumnModel({ index: ColumnIndex.TYPE, tags: ['app-type'] }),
      new TableColumnModel({ index: ColumnIndex.DAY_ACTUAL, tags: ['app-actual'] }),
      new TableColumnModel({ index: ColumnIndex.DAY_EXPECTED, tags: ['app-expected'] }),
      new TableColumnModel({ index: ColumnIndex.BALANCE, tags: ['app-balance'] }),
      new TableColumnModel({ index: ColumnIndex.WEEK_HOURS, tags: ['app-week-hours'] }),
      new TableColumnModel({ index: ColumnIndex.PROJECT, tags: ['app-project'] }),
      new TableColumnModel({ index: ColumnIndex.COMMENT, tags: ['app-comment'] }),
      new TableColumnModel({ index: ColumnIndex.OVERTIME, tags: ['app-overtime'] }),
      new TableColumnModel({ index: ColumnIndex.OVERTIME_50, tags: ['app-overtime50'] }),
      new TableColumnModel({ index: ColumnIndex.OVERTIME_100, tags: ['app-overtime100'] }),
    ];
    return this.refreshColumnModels(columns);
  }

  private refreshColumnModels(columns: TableColumnModel[]): TableColumnModel[] {
    columns.forEach((column) => column.isHidden = this.hiddenColumns.has(column.index));
    return columns;
  }

  private createRowModels() {
    const rows = [
      new TableRowModel({ index: 0, tags: ['app-time-record-table-header'] })
    ];
    return this.refreshRowModels(rows);
  }

  private refreshRowModels(rows: TableRowModel[]): TableRowModel[] {
    const timeRecordList = this.viewModel.timeRecordList;
    if (!timeRecordList || !this.viewModel.year || !this.viewModel.month) {
      return rows;
    }
    if (rows.length > timeRecordList.size + 1) {
      rows.length = timeRecordList.size + 1;
    }
    let previousTimeRecord: TimeRecordModel = null;
    timeRecordList.forEach((timeRecord: TimeRecordModel, index: number) => {
      const row = index + 1 < rows.length
        ? rows[index + 1]
        : rows[index + 1] = new TableRowModel({ index: index + 1 });
      const dayOfWeek = this.getDayOfWeek(timeRecord);
      const tags = [`app-weekday-${dayOfWeek}`];
      if (this.isStartOfWeek(dayOfWeek, previousTimeRecord)) {
        tags.push('app-start-of-week');
      }
      if (this.getPublicHoliday(timeRecord.date) !== null) {
        tags.push('app-public-holiday');
      }
      row.setValues({ tags });
      previousTimeRecord = timeRecord;
    });
    return rows;
  }

  private createCellModels() {
    const cells = [
      new TableCellModel({ row: 0, column: ColumnIndex.INSERT_ROW, content: this.createVisibleColumnsDropdown() }),
      new TableCellModel({ row: 0, column: ColumnIndex.REMOVE_ROW, content: null }),
      new TableCellModel({ row: 0, column: ColumnIndex.DAY, content: ColumnKey[ColumnIndex.DAY] }),
      new TableCellModel({ row: 0, column: ColumnIndex.FROM, content: ColumnKey[ColumnIndex.FROM] }),
      new TableCellModel({ row: 0, column: ColumnIndex.TO, content: ColumnKey[ColumnIndex.TO] }),
      new TableCellModel({ row: 0, column: ColumnIndex.HOURS, content: ColumnKey[ColumnIndex.HOURS] }),
      new TableCellModel({ row: 0, column: ColumnIndex.TYPE, content: ColumnKey[ColumnIndex.TYPE] }),
      new TableCellModel({ row: 0, column: ColumnIndex.DAY_ACTUAL, content: ColumnKey[ColumnIndex.DAY_ACTUAL] }),
      new TableCellModel({ row: 0, column: ColumnIndex.DAY_EXPECTED, content: ColumnKey[ColumnIndex.DAY_EXPECTED] }),
      new TableCellModel({ row: 0, column: ColumnIndex.BALANCE, content: ColumnKey[ColumnIndex.BALANCE] }),
      new TableCellModel({ row: 0, column: ColumnIndex.WEEK_HOURS, content: ColumnKey[ColumnIndex.WEEK_HOURS] }),
      new TableCellModel({ row: 0, column: ColumnIndex.PROJECT, content: ColumnKey[ColumnIndex.PROJECT] }),
      new TableCellModel({ row: 0, column: ColumnIndex.COMMENT, content: ColumnKey[ColumnIndex.COMMENT] }),
      new TableCellModel({ row: 0, column: ColumnIndex.OVERTIME, content: ColumnKey[ColumnIndex.OVERTIME] }),
      new TableCellModel({ row: 0, column: ColumnIndex.OVERTIME_50, content: ColumnKey[ColumnIndex.OVERTIME_50] }),
      new TableCellModel({ row: 0, column: ColumnIndex.OVERTIME_100, content: ColumnKey[ColumnIndex.OVERTIME_100] }),
    ];
    const timeRecordList = this.viewModel.timeRecordList;
    if (!timeRecordList) {
      return cells;
    }
    timeRecordList.forEach((timeRecord: TimeRecordModel, index: number) => {
      const previousTimeRecord = index > 0 ? timeRecordList.get(index - 1) : null;
      const isFirstRecordOfDay = !previousTimeRecord || previousTimeRecord.date !== timeRecord.date;
      const isFirstRecordOfWeek = this.isStartOfWeek(this.getDayOfWeek(timeRecord), previousTimeRecord);
      const row = index + 1;
      const rowCells: TableCellModel[] = [
        this.createInsertRowCellModel(row, ColumnIndex.INSERT_ROW),
        this.createRemoveRowCellModel(row, ColumnIndex.REMOVE_ROW),
        this.createDayValueCell(row, ColumnIndex.DAY, timeRecord, isFirstRecordOfDay, isFirstRecordOfWeek),
        this.createFromInputCell(row, ColumnIndex.FROM, timeRecord),
        this.createToInputCell(row, ColumnIndex.TO, timeRecord),
        this.createHoursInputCell(row, ColumnIndex.HOURS, timeRecord),
        this.createTypeInputCell(row, ColumnIndex.TYPE, timeRecord),
        this.createActualDayHoursValueCell(row, ColumnIndex.DAY_ACTUAL, isFirstRecordOfDay),
        this.createExpectedDayHoursInputCell(row, ColumnIndex.DAY_EXPECTED, timeRecord, isFirstRecordOfDay),
        this.createBalanceValueCell(row, ColumnIndex.BALANCE, isFirstRecordOfDay),
        this.createActualWeekHoursValueCell(row, ColumnIndex.WEEK_HOURS, isFirstRecordOfDay),
        this.createProjectInputCell(row, ColumnIndex.PROJECT, timeRecord),
        this.createCommentInputCell(row, ColumnIndex.COMMENT, timeRecord),
        this.createOvertimeInputCell(row, ColumnIndex.OVERTIME, timeRecord, 'overtime'),
        this.createOvertimeInputCell(row, ColumnIndex.OVERTIME_50, timeRecord, 'overtime50'),
        this.createOvertimeInputCell(row, ColumnIndex.OVERTIME_100, timeRecord, 'overtime100'),
      ];
      cells.push(...rowCells);
      this.refreshRowCells(row, rowCells, timeRecord, isFirstRecordOfDay, isFirstRecordOfWeek || !previousTimeRecord);
    });
    return cells;
  }

  private createVisibleColumnsDropdown(): DropdownModel {
    return new DropdownModel({
      tags: ['app-visible-columns-dropdown'],
      items: [
        this.createVisibleColumnDropdownItemModel(ColumnIndex.FROM),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.TO),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.HOURS),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.TYPE),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.DAY_ACTUAL),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.DAY_EXPECTED),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.BALANCE),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.WEEK_HOURS),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.PROJECT),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.COMMENT),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.OVERTIME),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.OVERTIME_50),
        this.createVisibleColumnDropdownItemModel(ColumnIndex.OVERTIME_100),
      ]
    });
  }

  private createVisibleColumnDropdownItemModel(columnIndex: number): ActionModel {
    const columnName = this.textService.getText(ColumnKey[columnIndex])
      .replace('-\n', '')
      .replace('\n', ' ');
    return new ActionModel({
      type: ViewComponentKey.LINK,
      tags: ['dropdown-item'],
      content: `${columnName} ${!this.hiddenColumns.has(columnIndex) ? '\u2714' : ''}`,
      onClick: (event: Event, model: ActionModel) => {
        if (this.hiddenColumns.has(columnIndex)) {
          this.hiddenColumns.delete(columnIndex);
          model.content = `${columnName} \u2714` as any;
          this.componentModel.getColumn(columnIndex).isHidden = false;
        } else {
          this.hiddenColumns.add(columnIndex);
          model.content = columnName as any;
          this.componentModel.getColumn(columnIndex).isHidden = true;
        }
        this.saveHiddenColumns(this.hiddenColumns);
        event.stopPropagation();
      }
    });
  }

  private createDayValueCell(
    row: number, column: number, timeRecord: TimeRecordModel, isFirstRecordOfDay: boolean, isFirstRecordOfWeek
  ): TableCellModel {
    const momentDate = moment(timeRecord.date);
    const items: ComponentModel[] = [
      new ValueModel({
        tags: ['app-form-value'],
        value: isFirstRecordOfDay ? momentDate.toDate() : null,
        format: 'table-day',
        alignment: Alignments.RIGHT,
      })
    ];
    if (isFirstRecordOfWeek) {
      items.push(new TextModel({
        tags: ['app-week-of-year'],
        text: `KW ${momentDate.isoWeek()}`
      }));
    }
    return new TableCellModel({
      row, column, content: new ContainerModel({ items })
    });
  }

  private createFromInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    return this.createInputCell(row, column, new TextInputModel({
      formattedValue: timeRecord.from,
      format: 'time',
      alignment: Alignments.RIGHT,
      onValueChanged: (value, originalValue, model) => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        const from = model.formattedValue;
        const updateValues: { [key: string]: any; } = {
          from: from || null
        };
        if (timeRecord.to) {
          const hours = this.timeRecordingService.calculateHours(from, timeRecord.to);
          if (hours !== null) {
            updateValues.hours = hours;
          }
        } else if (timeRecord.hours !== null) {
          const to = this.timeRecordingService.calculateTo(from, timeRecord.hours);
          if (to !== null) {
            updateValues.to = to;
          }
        }
        timeRecord.setValues(updateValues);
      }
    }));
  }

  private createToInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    return this.createInputCell(row, column, new TextInputModel({
      formattedValue: timeRecord.to,
      format: 'time',
      alignment: Alignments.RIGHT,
      onValueChanged: (value, originalValue, model) => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        const to = model.formattedValue;
        const updateValues: { [key: string]: any; } = {
          to: to || null
        };
        if (timeRecord.from) {
          const hours = this.timeRecordingService.calculateHours(timeRecord.from, to);
          if (hours !== null) {
            updateValues.hours = hours;
          }
        } else if (timeRecord.hours !== null) {
          const from = this.timeRecordingService.calculateFrom(to, timeRecord.hours);
          if (from !== null) {
            updateValues.from = from;
          }
        }
        timeRecord.setValues(updateValues);
      }
    }));
  }

  private createHoursInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    return this.createInputCell(row, column, new TextInputModel({
      value: timeRecord.hours,
      format: 'hours',
      suffix: 'h',
      alignment: Alignments.RIGHT,
      onValueChanged: value => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        const updateValues: { [key: string]: any; } = {
          hours: value
        };
        if (value === null) {
          updateValues.to = null;
          updateValues.from = null;
        } else {
          if (timeRecord.from) {
            const to = this.timeRecordingService.calculateTo(timeRecord.from, value);
            if (to !== null) {
              updateValues.to = to;
            }
          } else if (timeRecord.to) {
            const from = this.timeRecordingService.calculateFrom(timeRecord.to, value);
            if (from !== null) {
              updateValues.from = from;
            }
          }
        }
        timeRecord.setValues(updateValues);
      }
    }));
  }

  private createTypeInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    if (this.viewModel.printView) {
      return new TableCellModel({
        row, column, content: new TextModel({
          text: timeRecord.type ? `time-recording/model/time-record-type/value/${timeRecord.type}` : '-'
        })
      });
    }
    return this.createInputCell(row, column, new SelectInputModel({
      value: timeRecord.type,
      options: this.timeRecordTypeSelectOptions.values,
      formattedOptions: this.timeRecordTypeSelectOptions.formattedValues,
      onValueChanged: value => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        const updateValues: any = { type: value };
        if (value !== TimeRecordTypes.WORK) {
          if (!value) {
            updateValues.from = null;
            updateValues.to = null;
            updateValues.hours = null;
          }
          updateValues['overtime'] = null;
          updateValues['overtime50'] = null;
          updateValues['overtime100'] = null;
        }
        timeRecord.setValues(updateValues);
      }
    }));
  }

  private createActualDayHoursValueCell(row: number, column: number, isFirstRecordOfDay: boolean): TableCellModel {
    return new TableCellModel({
      row, column, content: new ValueModel({
        tags: { 'app-is-empty': true, 'app-form-value': true },
        value: null,
        format: 'number-2',
        suffix: ' ',
        alignment: Alignments.RIGHT,
        isHidden: !isFirstRecordOfDay,
        onValueChanged: (value, originalValue, model: ValueModel) => {
          model.tags.set('app-is-empty', value === null);
          model.suffix = hasValue(value) ? 'h' : ' ';
        },
      })
    });
  }

  private createExpectedDayHoursInputCell(
    row: number, column: number, timeRecord: TimeRecordModel, isFirstRecordOfDay: boolean
  ): TableCellModel {
    const originalTargetHours = isFirstRecordOfDay ? this.viewModel.targetHoursMap.get(timeRecord.date) : null;
    return this.createInputCell(row, column, new TextInputModel({
      value: originalTargetHours ? originalTargetHours.value : null,
      format: 'hours',
      suffix: 'h',
      alignment: Alignments.RIGHT,
      isHidden: !isFirstRecordOfDay,
      onValueChanged: (value) => {
        let targetHours = this.viewModel.targetHoursMap.get(timeRecord.date);
        if (!targetHours) {
          if (!hasValue(value)) {
            return;
          }
          this.viewModel.targetHoursMap.set(timeRecord.date, targetHours = new TargetHoursModel({
            employeeId: this.viewModel.employeeId,
            type: TargetHoursTypes.WORK,
            date: timeRecord.date
          }));
        }
        targetHours.value = value;
      },
    }));
  }

  private createBalanceValueCell(row: number, column: number, isFirstRowOfDay: boolean): TableCellModel {
    return new TableCellModel({
      row, column, content: new ValueModel({
        tags: { 'app-form-value': true },
        value: undefined,  // undefined does not display null format value '-'
        format: 'number-2',
        suffix: ' ',
        alignment: Alignments.RIGHT,
        isHidden: !isFirstRowOfDay,
        onValueChanged: (value, originalValue, model: ValueModel) => {
          model.suffix = hasValue(value) ? 'h' : ' ';
        },
      })
    });
  }

  private createActualWeekHoursValueCell(row: number, column: number, isFirstRecordOfWeek: boolean): TableCellModel {
    return new TableCellModel({
      row, column, content: new ValueModel({
        tags: { 'app-is-empty': true, 'app-form-value': true },
        value: null,
        format: 'number-2',
        suffix: ' ',
        alignment: Alignments.RIGHT,
        isHidden: !isFirstRecordOfWeek,
        onValueChanged: (value, originalValue, model: ValueModel) => {
          model.tags.set('app-is-empty', value === null);
          model.suffix = hasValue(value) ? 'h' : ' ';
        },
      })
    });
  }

  private createProjectInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    const originalProject = timeRecord.projectId && this.viewModel.projectList.find(item => item.id === timeRecord.projectId);
    return this.createInputCell(row, column, new ProjectAutoCompleteInputModel({
      tags: {
        'app-project-not-confirmed': originalProject && !originalProject.isConfirmed,
        'app-project-not-visible': timeRecord.projectId && !originalProject
      },
      type: TimeRecordingComponentKey.PROJECT_INPUT,
      value: timeRecord.projectId
        ? (originalProject?.name
          ? this.resolveFullProjectName(originalProject)
          : this.projectNotVisibleText)
        : null,
      projects: this.sharedViewService.viewModel.projectList,
      alignment: Alignments.LEFT,
      context: originalProject,
      onValueChanged: async (value, _, model: AutoCompleteInputModel) => {
        if (!value) {
          model.tags.remove('app-project-not-confirmed');
          timeRecord.projectId = null;
          return;
        }
        model.tags.set('app-project-not-visible', value === this.projectNotVisibleText);
        if (value === this.projectNotVisibleText) {
          return;
        }

        const projectNames = value.split(':');

        let shouldRefresh = false;
        let project: Project | undefined;

        for (const name of projectNames) {
          if (!name) {
            break;
          }

          let currentProject = this.viewModel.projectList.find((p) => p.name === name && (project === undefined && !p.parentId || project.id === p.parentId));
          if (!currentProject) {
            currentProject = await this.sharedViewService.saveProject({ name, isConfirmed: false, isActive: true, parentId: project?.id });
            shouldRefresh = true;
          }

          project = currentProject;
        }

        if (shouldRefresh) {
          await this.sharedViewService.refreshProjectList();
        }

        model.context = project;
        timeRecord.projectId = project?.id;
      },
      validation: [
        { [TimeRecordingValidationKey.TIME_RECORD_PROJECT]: { projectList: this.viewModel.projectList } },
      ]
    }));
  }

  private createCommentInputCell(row: number, column: number, timeRecord: TimeRecordModel): TableCellModel {
    return this.createInputCell(row, column, new TextInputModel({
      value: timeRecord.comment,
      alignment: Alignments.LEFT,
      onValueChanged: (value) => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        timeRecord.comment = value;
      },
    }));
  }

  private createOvertimeInputCell(row: number, column: number, timeRecord: TimeRecordModel, inputKey: string): TableCellModel {
    return this.createInputCell(row, column, new TextInputModel({
      value: timeRecord[inputKey],
      format: 'hours',
      suffix: 'h',
      alignment: Alignments.RIGHT,
      // validation: {
      //   'overtime': () => this.validateOvertime(row - 1)
      // },
      onValueChanged: (value) => {
        if (this.refreshingRows.has(row)) {
          return; // do not re-apply value if it is refreshed from model
        }
        timeRecord[inputKey] = value;
      },
    }));
  }

  // TODO
  // private validateOvertime(recordIndex: number): boolean {
  //   nicht größer als stunden (Zeile)
  //   nicht mehr als stunden - soll (Tag)
  //   return true;
  // }

  private createInputCell(row: number, column: number, inputModel: ValueModel): TableCellModel {
    const suffix = inputModel.suffix;
    const keydownListener = inputModel.onKeydown?.bind({}) ?? (() => {
    });
    inputModel.setValues({
      placeholder: inputModel.isFocused ? '' : ' - ',
      suffix: (!suffix || hasValue(inputModel.value)) ? suffix : ' ',
      onChanged: (event: ChangedEvent, model: ValueModel) => {
        if (suffix && event.changes['value']) {
          model.suffix = hasValue(model.value) ? suffix : ' ';
        }
        if (event.changes['isFocused']) {
          model.setValues({ placeholder: model.isFocused ? '' : ' - ' });
        }
      },
      onKeydown: (event, model) => {
        this.onInputCellKeydown(event, row, column);
        keydownListener(event, model);
      }
    });
    return new TableCellModel({ row, column, content: inputModel });
  }

  private createInsertRowCellModel(row: number, column: number): TableCellModel {
    return new TableCellModel({
      row, column, content: new ActionModel({
        tags: ['app-form-btn', 'btn-sm', 'btn-outline-secondary'],
        content: '+',
        onClick: () => {
          this.insertRow(row);
          this.componentModel.getCell(row + 1, ColumnIndex.FROM).content.isFocused = true;
        },
        onKeydown: (event) => this.onInputCellKeydown(event, row, column)
      })
    });
  }

  private createRemoveRowCellModel(row: number, column: number): TableCellModel {
    return new TableCellModel({
      row, column, content: new ActionModel({
        tags: ['app-form-btn', 'app-form', 'btn-sm', 'btn-outline-secondary'],
        content: '-',
        onClick: () => {
          this.removeRow(row);
          this.componentModel.getCell(row > 1 ? row - 1 : row, ColumnIndex.INSERT_ROW).content.isFocused = true;
        },
        onKeydown: (event) => this.onInputCellKeydown(event, row, column)
      })
    });
  }

  private isStartOfWeek(dayOfWeek: number, previousRecord: TimeRecordModel): boolean {
    if (dayOfWeek !== 1) {
      return false;
    }
    if (!previousRecord) {
      return true;
    }
    const previousDayOfWeek = this.getDayOfWeek(previousRecord);
    return previousDayOfWeek !== dayOfWeek;
  }

  private getDayOfWeek(timeRecord: TimeRecordModel): number {
    // iso day of week: 1 -> Monday, ..., 7 -> Sunday
    return moment(timeRecord.date).isoWeekday();
  }

  private getPublicHoliday(date: string): PublicHoliday {
    if (!this.viewModel.publicHolidayList) {
      return null;
    }
    return this.viewModel.publicHolidayList.find((item) => item.date === date) || null;
  }

  private insertRow(row: number) {
    const index = row - 1;
    const originalRecordModelList = this.viewModel.timeRecordList;
    if (index < 0 || index > originalRecordModelList.size - 1) {
      throw new Error(`Invalid index '${index}'`);
    }
    const recordModelList = originalRecordModelList.toArray();
    const date = recordModelList[index].date;
    const publicHoliday = this.getPublicHoliday(date);
    const recordModel = new TimeRecordModel({
      employeeId: this.viewModel.employeeId,
      date,
      comment: publicHoliday ? publicHoliday.name : null
    });
    recordModelList.splice(index + 1, 0, recordModel);
    this.viewModel.timeRecordList.setValues(recordModelList, { exclusive: true });
  }

  private removeRow(row: number) {
    const index = row - 1;
    const originalRecordModelList = this.viewModel.timeRecordList;
    if (index < 0 || index > originalRecordModelList.size - 1) {
      throw new Error(`Invalid index '${index}'`);
    }
    const recordModelList = this.viewModel.timeRecordList.toArray();
    const timeRecord = recordModelList[index];
    const isLastDayTimeRecord = !recordModelList.find(item => item.date === timeRecord.date && item !== timeRecord);
    if (!isLastDayTimeRecord) {
      recordModelList.splice(index, 1);
    } else {
      const publicHoliday = this.getPublicHoliday(timeRecord.date);
      recordModelList.splice(index, 1, new TimeRecordModel({
        employeeId: timeRecord.employeeId,
        date: timeRecord.date,
        comment: publicHoliday ? publicHoliday.name : null
      }));
    }
    this.viewModel.timeRecordList.setValues(recordModelList, { exclusive: true });
    if (timeRecord.modified) {
      this.sharedViewService.deleteTimeRecord(timeRecord);
    } else if (isLastDayTimeRecord) {
      this.sharedViewService.clearDefaultWorkTargetHours(timeRecord.date);
    }
  }

  private refreshRow(
    recordModel: TimeRecordModel, recordIndex: number, isFirstRowOfDay: boolean, isFirstRowOfWeek: boolean
  ) {
    const row = recordIndex + 1;
    if (row >= this.componentModel.cellArray.length) {
      return;
    }
    this.refreshRowCells(row, this.componentModel.cellArray[row], recordModel, isFirstRowOfDay, isFirstRowOfWeek);
  }

  private refreshRowCells(
    row: number, rowCells: TableCellModel[], model: TimeRecordModel, isFirstRowOfDay: boolean, isFirstRowOfWeek: boolean
  ) {
    this.refreshingRows.add(row);
    rowCells[ColumnIndex.FROM].content.set('formattedValue', model.from);
    rowCells[ColumnIndex.TO].content.set('formattedValue', model.to);
    rowCells[ColumnIndex.HOURS].content.set('value', model.hours);
    rowCells[ColumnIndex.TYPE].content.set('value', model.type);
    if (isFirstRowOfDay) {
      rowCells[ColumnIndex.DAY_ACTUAL].content.set('value', this.calculateActualDayHours(row - 1));
    }
    if (isFirstRowOfDay) {
      rowCells[ColumnIndex.BALANCE].content.set('value', this.calculateDayBalanceHours(row - 1, model.date));
    }
    if (isFirstRowOfWeek) {
      rowCells[ColumnIndex.WEEK_HOURS].content.set('value', this.calculateActualWeekHours(row - 1));
    }
    if (!model.projectId) {
      rowCells[ColumnIndex.PROJECT].content.set('value', null);
    } else {
      const project = this.viewModel.projectList.find(item => item.id === model.projectId);
      if (!project?.name) {
        rowCells[ColumnIndex.PROJECT].content.set('value', this.projectNotVisibleText);
      } else {
        rowCells[ColumnIndex.PROJECT].content.set('value', this.resolveFullProjectName(project));
      }
    }
    rowCells[ColumnIndex.COMMENT].content.set('value', model.comment);
    rowCells[ColumnIndex.OVERTIME].content.setValues({
      value: model.overtime,
      isHidden: model.type && model.type !== TimeRecordTypes.WORK
    });
    rowCells[ColumnIndex.OVERTIME_50].content.setValues({
      value: model.overtime50,
      isHidden: model.type && model.type !== TimeRecordTypes.WORK
    });
    rowCells[ColumnIndex.OVERTIME_100].content.setValues({
      value: model.overtime100,
      isHidden: model.type && model.type !== TimeRecordTypes.WORK
    });
    this.refreshingRows.delete(row);
    rowCells.forEach((cell) => {
      if (cell.content instanceof ValueModel) {
        cell.content.validate();
      }
    });
  }

  private calculateActualDayHours(recordIndex: number): number {
    return this.timeRecordingService.calculateActualHours(this.getRecordsOfDay(recordIndex));
  }

  private calculateActualWeekHours(recordIndex: number): number {
    const recordsOfWeek = this.getRecordsOfWeek(recordIndex);
    let actualWeekHours = this.timeRecordingService.calculateActualHours(recordsOfWeek);
    if (recordsOfWeek.length > 0) {
      const firstRecordOfWeek = recordsOfWeek[0];
      if (firstRecordOfWeek === this.viewModel.timeRecordList.get(0)
        && this.getDayOfWeek(firstRecordOfWeek) !== 1
        && this.viewModel.carryover.weekHours) {
        actualWeekHours = (actualWeekHours || 0) + this.viewModel.carryover.weekHours;
      }
    }
    return actualWeekHours;
  }

  private calculateDayBalanceHours(recordIndex: number, date: string): number {
    const recordsOfDay: TimeRecordModel[] = this.getRecordsOfDay(recordIndex);
    const targetHoursModel = this.viewModel.targetHoursMap.get(date);
    const expectedHours = targetHoursModel && targetHoursModel.value;
    const actualHours = this.timeRecordingService.calculateActualHours(recordsOfDay);
    const compTimeHours = this.timeRecordingService.calculateTotal(
      recordsOfDay.filter((record) => record.type === TimeRecordTypes.COMP_TIME), TimeRecordFieldKey.HOURS
    );
    const overtimeHours = this.calculateOvertimeHours(recordsOfDay);
    if (!hasValue(actualHours) && !hasValue(expectedHours) && !hasValue(compTimeHours) && !hasValue(overtimeHours)) {
      return null;
    }
    return (actualHours || 0) - (expectedHours || 0) - (compTimeHours || 0) - (overtimeHours || 0);
  }

  private calculateOvertimeHours(records: TimeRecordModel[]): number {
    return records.reduce((total: number, record) => {
      if (hasValue(record.overtime)) {
        total = total ? total + record.overtime : record.overtime;
      }
      if (hasValue(record.overtime50)) {
        total = total ? total + record.overtime50 : record.overtime50;
      }
      if (hasValue(record.overtime100)) {
        total = total ? total + record.overtime100 : record.overtime100;
      }
      return total;
    }, null);
  }

  private getRecordsOfDay(recordIndex: number): TimeRecordModel[] {
    const firstRecordIndex = this.getIndexOfFirstRecordOfDay(recordIndex);
    if (firstRecordIndex < 0) {
      return [];
    }
    const firstRecord = this.viewModel.timeRecordList.get(firstRecordIndex);
    const recordsOfDay = [firstRecord];
    for (let i = firstRecordIndex + 1; i < this.viewModel.timeRecordList.size; i++) {
      const nextRecord = this.viewModel.timeRecordList.get(i);
      if (!nextRecord || nextRecord.date !== firstRecord.date) {
        break;
      }
      recordsOfDay.push(nextRecord);
    }
    return recordsOfDay;
  }

  private getIndexOfFirstRecordOfDay(recordIndex: number): number {
    const record = this.viewModel.timeRecordList.get(recordIndex);
    if (!record) {
      return -1;
    }
    for (let i = recordIndex; i > 0; i--) {
      const previousRecord: TimeRecordModel = this.viewModel.timeRecordList.get(i - 1);
      if (!previousRecord || previousRecord.date !== record.date) {
        return i;
      }
    }
    return 0;
  }

  private getRecordsOfWeek(recordIndex: number): TimeRecordModel[] {
    const firstRecordIndex = this.getIndexOfFirstRecordOfWeek(recordIndex);
    if (firstRecordIndex < 0) {
      return [];
    }
    const firstRecord = this.viewModel.timeRecordList.get(firstRecordIndex);
    const recordsOfWeek = [firstRecord];
    for (let i = firstRecordIndex + 1; i < this.viewModel.timeRecordList.size; i++) {
      const nextRecord = this.viewModel.timeRecordList.get(i);
      if (!nextRecord || nextRecord.date !== firstRecord.date && this.getDayOfWeek(nextRecord) === 1) {
        break;
      }
      recordsOfWeek.push(nextRecord);
    }
    return recordsOfWeek;
  }

  private getIndexOfFirstRecordOfWeek(recordIndex: number): number {
    let record = this.viewModel.timeRecordList.get(recordIndex);
    if (!record) {
      return -1;
    }
    for (let i = recordIndex; i > 0; i--) {
      const previousRecord: TimeRecordModel = this.viewModel.timeRecordList.get(i - 1);
      if (!previousRecord || previousRecord.date !== record.date && this.getDayOfWeek(record) === 1) {
        return i;
      }
      record = previousRecord;
    }
    return 0;
  }

  private onInputCellKeydown(event: KeyboardEvent, row: number, column: number) {
    if (event.key === KeyCodes.UP.key) {
      this.getAboveInputCell(row, column).content.isFocused = true;
      event.preventDefault();
    } else if (event.key === KeyCodes.DOWN.key) {
      this.getBelowInputCell(row, column).content.isFocused = true;
      event.preventDefault();
    } else if (event.key === KeyCodes.RIGHT.key) {
      const inputElement = event.currentTarget;
      if (
        event.ctrlKey && event.altKey ||
        inputElement['selectionStart'] === undefined ||
        inputElement['selectionStart'] === inputElement['value'].length
      ) {
        this.getRightInputCell(row, column).content.isFocused = true;
        event.preventDefault();
      }
    } else if (event.key === KeyCodes.LEFT.key) {
      const inputElement = event.currentTarget;
      if (
        event.ctrlKey && event.altKey ||
        inputElement['selectionEnd'] === undefined ||
        inputElement['selectionEnd'] === 0
      ) {
        this.getLeftInputCell(row, column).content.isFocused = true;
        event.preventDefault();
      }
    } else if (event.key === KeyCodes.ENTER.key) {
      const inputElement = event.currentTarget;
      const inputModel = this.componentModel.getCell(row, column).content as ValueModel;
      if (inputModel.isValid && inputModel.formattedValue === inputElement['value']) {
        this.getNextInputCell(row, column).content.isFocused = true;
        event.preventDefault();
      }
    }
  }

  private getAboveInputCell(row: number, column: number): TableCellModel {
    const aboveRow = row > 1 ? row - 1 : this.componentModel.rowCount - 1;
    const aboveCell = this.componentModel.getCell(aboveRow, column);
    return this.isFocusableCell(aboveCell) ? aboveCell : this.getAboveInputCell(aboveRow, column);
  }

  private getBelowInputCell(row: number, column: number): TableCellModel {
    const belowRow = row < this.componentModel.rowCount - 1 ? row + 1 : 1;
    const belowCell = this.componentModel.getCell(belowRow, column);
    return this.isFocusableCell(belowCell) ? belowCell : this.getBelowInputCell(belowRow, column);
  }

  private getLeftInputCell(row: number, column: number): TableCellModel {
    const leftColumn = column > 1 ? column - 1 : this.componentModel.columnCount - 1;
    const leftCell = this.componentModel.getCell(row, leftColumn);
    return this.isFocusableCell(leftCell) ? leftCell : this.getLeftInputCell(row, leftColumn);
  }

  private getRightInputCell(row: number, column: number): TableCellModel {
    const rightColumn = column < this.componentModel.columnCount - 1 ? column + 1 : 1;
    const rightCell = this.componentModel.getCell(row, rightColumn);
    return this.isFocusableCell(rightCell) ? rightCell : this.getRightInputCell(row, rightColumn);
  }

  private isFocusableCell(cell: TableCellModel): boolean {
    const content = cell.content;
    return content.tabIndex >= 0 && !content.isDisabled && !content.isHidden && !this.componentModel.getColumn(cell.column).isHidden;
  }

  private getNextInputCell(row: number, column: number): TableCellModel {
    let nextColumn: number;
    let nextRow: number;
    if (column >= ColumnIndex.TYPE) {
      nextColumn = 1;
      nextRow = row < this.componentModel.rowCount - 1 ? row + 1 : 1;
    } else {
      nextColumn = column + 1;
      nextRow = row;
    }
    return this.componentModel.getCell(nextRow, nextColumn);
  }

  private loadHiddenColumns(): Set<number> {
    const hiddenColumns = this.activatedRoute.snapshot.queryParamMap.get('hiddenColumns')
      || this.persistenceService.getValue(TimeRecordingPersistenceKey.HIDDEN_COLUMNS);
    if (!hiddenColumns) {
      return new Set<number>();
    }
    try {
      return new Set((JSON.parse(hiddenColumns) as string[])
        .filter((columnName) => columnName in ColumnIndex)
        .map((columnName) => ColumnIndex[columnName]));
    } catch {
      return new Set<number>();
    }
  }

  private saveHiddenColumns(hiddenColumns: Set<number>) {
    const columnNames = Object.keys(ColumnIndex);
    const hiddenColumnArray: string[] = [];
    for (const columnIndex of hiddenColumns) {
      hiddenColumnArray.push(columnNames[columnIndex]);
    }
    this.persistenceService.setValue(TimeRecordingPersistenceKey.HIDDEN_COLUMNS, JSON.stringify(hiddenColumnArray));
  }

  private resolveFullProjectName(project: Project): string {
    if (project.parentId === null) {
      return project.name;
    }

    const parent = this.viewModel.projectList.find((p) => p.id === project.parentId);

    return `${this.resolveFullProjectName(parent)}:${project.name}`;
  }
}
